import { motion } from "framer-motion";
import { Building2, MapPin, TrendingUp, Eye, Phone, Star, ArrowRight, Users, Camera } from "lucide-react";

const STATS = [
  { value: "+130%", label: "Prenotazioni 18-34 anni", icon: TrendingUp },
  { value: "2x", label: "Visite fisiche dopo il tour", icon: Users },
  { value: "+30%", label: "Click su prenota/chiama", icon: Phone },
  { value: "5-10x", label: "Tempo sul tuo profilo Google", icon: Eye },
];

const BIG_PLAYERS = [
  { name: "Poderi Arcangelo", zone: "San Gimignano", reviews: "1.125 recensioni", type: "Agriturismo" },
  { name: "Tenuta Guardastelle", zone: "San Gimignano", reviews: "1.018 recensioni", type: "Agriturismo" },
  { name: "Agriturismo Nerbona", zone: "Colle Val d'Elsa", reviews: "218 recensioni, N.1 qualita/prezzo", type: "Agriturismo" },
  { name: "La Canonica", zone: "Colle Val d'Elsa", reviews: "1.723 recensioni, 9.3/10", type: "Ristorante" },
  { name: "Il Castelletto", zone: "Colle Val d'Elsa", reviews: "1.368 recensioni, 9.2/10", type: "Ristorante" },
  { name: "Tenuta di Mensanello", zone: "Colle Val d'Elsa", reviews: "Piscina, vini, eventi", type: "Agriturismo" },
  { name: "Agriturismo Cesani", zone: "San Gimignano", reviews: "363 recensioni, panorama mozzafiato", type: "Agriturismo" },
  { name: "Tenuta Sovestro", zone: "San Gimignano", reviews: "Ristorante + piscina", type: "Agriturismo" },
];

const WA_MSG = "Ciao Riccardo! Ho un hotel/ristorante/agriturismo in Toscana e vorrei saperne di piu sul Virtual Tour 360.";

export default function LandingHotel() {
  return (
    <div className="min-h-screen bg-[#0c1220] text-white">
      <div className="max-w-4xl mx-auto px-4 py-10">

        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#EA4335]/10 border border-[#EA4335]/30 text-[#EA4335] text-sm font-bold mb-6">
            <Camera className="w-4 h-4" /> HORECA — Hotel, Ristoranti, B&B, Agriturismi
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-6">
            I tuoi ospiti decidono <br />
            <span className="text-[#4285F4]">prima di arrivare.</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Il 87% dei turisti esplora virtualmente prima di prenotare.
            Se sul tuo profilo Google vedono 3 foto sfocate del 2019, <strong className="text-white">prenotano dal tuo concorrente.</strong>
          </p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {STATS.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
              className="bg-white/5 border border-white/10 rounded-2xl p-5 text-center">
              <s.icon className="w-5 h-5 text-[#4285F4] mx-auto mb-2" />
              <div className="text-3xl font-black text-white">{s.value}</div>
              <div className="text-xs text-gray-400 mt-1">{s.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Pain Point */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          className="bg-[#EA4335]/10 border border-[#EA4335]/30 rounded-2xl p-6 mb-12">
          <h2 className="text-xl font-bold text-[#EA4335] mb-3">Il problema che non vedi</h2>
          <p className="text-gray-300 text-base leading-relaxed">
            I tuoi concorrenti su Booking, Airbnb e Google Maps hanno le stesse foto piatte che hai tu.
            Ma il primo che mette un <strong className="text-white">Virtual Tour 360 immersivo</strong> si prende
            il <strong className="text-white">130% in piu di prenotazioni</strong> dalla fascia 18-34 anni — quella che spende di piu in Toscana.
            <br /><br />
            Tu hai una struttura bellissima. <strong className="text-white">Falla vedere come merita.</strong>
          </p>
        </motion.div>

        {/* CTA Primary */}
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4 }}
          className="bg-gradient-to-r from-[#34A853]/20 to-[#4285F4]/20 border border-[#34A853]/30 rounded-2xl p-8 text-center mb-12">
          <h2 className="text-2xl font-black mb-2">Pacchetti da €99 — Deducibili al 100%</h2>
          <p className="text-gray-300 mb-6">PayPal 3 rate TAEG 0%. Timewarp Hyperlapse IN REGALO.</p>
          <a href={`https://wa.me/+393273839782?text=${encodeURIComponent(WA_MSG)}`}
            className="inline-flex items-center gap-2 bg-[#34A853] hover:bg-[#2d9249] text-white font-bold px-8 py-4 rounded-full text-lg transition-all hover:scale-105">
            <Phone className="w-5 h-5" /> Scrivimi su WhatsApp
          </a>
          <p className="text-gray-500 text-xs mt-3">Riccardo Rossi — Fotografo Google Certificato dal 2016</p>
        </motion.div>

        {/* Big Players */}
        <div className="mb-12">
          <h2 className="text-2xl font-black mb-2">I Big Player della Toscana</h2>
          <p className="text-gray-400 text-base mb-6">Queste strutture hanno migliaia di recensioni ma <strong className="text-[#FBBC04]">ancora nessun Virtual Tour 360.</strong> Il primo che si muove vince.</p>
          <div className="grid gap-3">
            {BIG_PLAYERS.map((b, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 + i * 0.05 }}
                className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-xl px-5 py-3">
                <Building2 className="w-5 h-5 text-[#4285F4] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold text-base">{b.name}</div>
                  <div className="text-gray-400 text-xs">{b.reviews}</div>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-500 flex-shrink-0">
                  <MapPin className="w-3 h-3" /> {b.zone}
                </div>
                <span className="px-2 py-0.5 rounded-full bg-white/10 text-[10px] text-gray-400 flex-shrink-0">{b.type}</span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Proof */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-12">
          <h3 className="text-lg font-bold text-white mb-3">Perche funziona in Toscana</h3>
          <ul className="space-y-2 text-gray-300 text-base">
            <li className="flex items-start gap-2"><Star className="w-4 h-4 text-[#FBBC04] mt-1 flex-shrink-0" /> Il turismo toscano vale <strong className="text-white">€27.5 miliardi</strong> (2025), in crescita del 9.9% annuo</li>
            <li className="flex items-start gap-2"><Star className="w-4 h-4 text-[#FBBC04] mt-1 flex-shrink-0" /> Le ricerche "near me" sono cresciute del <strong className="text-white">900%</strong> — chi ti trova su Google decide in 30 secondi</li>
            <li className="flex items-start gap-2"><Star className="w-4 h-4 text-[#FBBC04] mt-1 flex-shrink-0" /> Il <strong className="text-white">28%</strong> di chi ti trova da mobile compra entro 24 ore</li>
            <li className="flex items-start gap-2"><Star className="w-4 h-4 text-[#FBBC04] mt-1 flex-shrink-0" /> Un Virtual Tour costa <strong className="text-white">€99</strong> con noi. Sul mercato parte da €500.</li>
          </ul>
        </div>

        {/* Final CTA */}
        <div className="text-center">
          <a href={`https://wa.me/+393273839782?text=${encodeURIComponent(WA_MSG)}`}
            className="inline-flex items-center gap-2 bg-[#EA4335] hover:bg-[#d33426] text-white font-bold px-8 py-4 rounded-full text-lg transition-all hover:scale-105">
            Voglio il Virtual Tour <ArrowRight className="w-5 h-5" />
          </a>
          <p className="text-gray-600 text-xs mt-4">schedabusiness.it — Fotografo Google Street View Certificato dal 2016</p>
        </div>
      </div>
    </div>
  );
}
